import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { isMapAlive } from './mapGuard';
import type { FeatureCollection } from 'geojson';

interface Props {
  map: mapboxgl.Map;
  pushbackData: FeatureCollection | null;
  visible: boolean;
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };
const SOURCE_ID = 'me-pushback-risk';

const PushbackRiskLayer: React.FC<Props> = ({ map, pushbackData, visible }) => {
  // Effect 1: add source + layers on mount
  useEffect(() => {
    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, { type: 'geojson', data: pushbackData ?? EMPTY_FC });
    }

    if (!map.getLayer('me-pushback-fill')) {
      map.addLayer({
        id: 'me-pushback-fill',
        type: 'fill',
        source: SOURCE_ID,
        paint: {
          'fill-color': '#ef5350',
          'fill-opacity': 0.2,
        },
      });
    }

    if (!map.getLayer('me-pushback-outline')) {
      map.addLayer({
        id: 'me-pushback-outline',
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': '#ef5350',
          'line-width': 1.5,
          'line-dasharray': [3, 2],
        },
      });
    }

    return () => {
      if (!isMapAlive(map)) return;
      if (map.getLayer('me-pushback-outline')) map.removeLayer('me-pushback-outline');
      if (map.getLayer('me-pushback-fill')) map.removeLayer('me-pushback-fill');
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Effect 2: sync source data
  useEffect(() => {
    const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (src) src.setData(pushbackData ?? EMPTY_FC);
  }, [map, pushbackData]);

  // Effect 3: toggle visibility
  useEffect(() => {
    const visibility = visible ? 'visible' : 'none';
    if (map.getLayer('me-pushback-fill')) {
      map.setLayoutProperty('me-pushback-fill', 'visibility', visibility);
    }
    if (map.getLayer('me-pushback-outline')) {
      map.setLayoutProperty('me-pushback-outline', 'visibility', visibility);
    }
  }, [map, visible]);

  return null;
};

export default PushbackRiskLayer;
